// Container-width hook. Returns the actual rendered width (px) of a DOM
// element. Updates whenever the element resizes — including when the
// host page's layout changes, when the viewport rotates, or when a
// sidebar opens/closes around it.
//
// Why this and not useMediaQuery: a widget embedded into a wide host
// page may still live inside a narrow column (e.g., a 60% main content
// area on a desktop site). Viewport-based queries would tell us
// "desktop" while the actual usable width is mobile-shaped. Container
// width is the truth.

import { useEffect, useState, type RefObject } from 'react';

export function useContainerWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState<number>(() => ref.current?.offsetWidth ?? 0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Set initial width on mount in case ResizeObserver doesn't fire immediately
    setWidth(el.offsetWidth);

    const obs = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      // contentBoxSize is the modern API, but fall back to contentRect for
      // older browsers (Safari < 15.4 etc.)
      const w =
        entry.borderBoxSize?.[0]?.inlineSize ??
        entry.contentRect.width;
      setWidth(Math.round(w));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);

  return width;
}
